import React from 'react';
import { H1, H3, colors, Icon } from '@tviklax/ui-components';
import { gql } from '@apollo/client';
import { useGraphQLQuery } from '@tviklax/ui-components';
import { useUser } from '../../../context/UserContext';
import {
  DashboardContainer,
  DashboardHeader,
  HeaderContent,
  Subtitle,
  LastUpdated,
  StatsGrid,
  StatCard,
  StatContent,
  StatDetails,
  StatTitle,
  StatValue,
  StatIcon,
  OrdersCard,
  OrdersHeader,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Amount,
} from './styles';

const AGENT_DASHBOARD_QUERY = gql`
  query AgentDashboard {
    vendors(limit: 500, offset: 0) {
      id
      firstName
      lastName
      email
      agentId
      agentCommission
    }
    subOrders(limit: 500, offset: 0) {
      id
      vendorId
      subOrderNo
      totalAmount
      subOrderStatus
      createdAt
    }
  }
`;

const formatAmount = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  const numericValue = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(numericValue)) return '—';
  return `$${numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const AgentDashboard: React.FC = () => {
  const { user } = useUser();
  const agentId = user?.agentId;

  const { data } = useGraphQLQuery<{
    vendors?: Array<{ id: number; firstName?: string; lastName?: string; email?: string; agentId?: number; agentCommission?: number }>;
    subOrders?: Array<{ id: number; vendorId?: number; subOrderNo?: string; totalAmount?: number; subOrderStatus?: number; createdAt?: string }>;
  }>(AGENT_DASHBOARD_QUERY, { skip: agentId === undefined });

  const managedVendors = (data?.vendors ?? []).filter((v) => v.agentId === agentId);
  const managedVendorIds = new Set(managedVendors.map((v) => v.id));
  const managedOrders = (data?.subOrders ?? []).filter((so) => so.vendorId !== undefined && managedVendorIds.has(so.vendorId));

  const ordersByVendor = new Map<number, { count: number; value: number }>();
  managedOrders.forEach((so) => {
    const key = so.vendorId as number;
    const existing = ordersByVendor.get(key) ?? { count: 0, value: 0 };
    existing.count += 1;
    existing.value += Number(so.totalAmount) || 0;
    ordersByVendor.set(key, existing);
  });

  const totalValue = managedOrders.reduce((sum, so) => sum + (Number(so.totalAmount) || 0), 0);

  return (
    <DashboardContainer>
      <DashboardHeader initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <HeaderContent>
          <H1 style={{ margin: '0' }}>Your Vendor Portfolio</H1>
          <Subtitle>The sellers you brought onto Tvikla, and what's moving through their orders.</Subtitle>
        </HeaderContent>
        <LastUpdated>
          <Icon name="Clock" size={16} />
          <span>Live updates enabled</span>
        </LastUpdated>
      </DashboardHeader>

      {agentId === undefined ? (
        <OrdersCard initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <span style={{ color: colors.gray500 }}>
            This login isn't linked to a real agent record yet, so there's no portfolio to show.
          </span>
        </OrdersCard>
      ) : (
        <>
          <StatsGrid>
            <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <StatContent>
                <StatDetails>
                  <StatTitle>Vendors Managed</StatTitle>
                  <StatValue>{managedVendors.length}</StatValue>
                </StatDetails>
                <StatIcon $color={colors.gray100}><Icon name="Building2" color={colors.primary} /></StatIcon>
              </StatContent>
            </StatCard>
            <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <StatContent>
                <StatDetails>
                  <StatTitle>Orders Across Portfolio</StatTitle>
                  <StatValue>{managedOrders.length}</StatValue>
                </StatDetails>
                <StatIcon $color={colors.gray100}><Icon name="Package" color={colors.primary} /></StatIcon>
              </StatContent>
            </StatCard>
            <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <StatContent>
                <StatDetails>
                  <StatTitle>Portfolio Value</StatTitle>
                  <StatValue>{formatAmount(totalValue)}</StatValue>
                </StatDetails>
                <StatIcon $color={colors.gray100}><Icon name="DollarSign" color={colors.primary} /></StatIcon>
              </StatContent>
            </StatCard>
          </StatsGrid>

          <OrdersCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <OrdersHeader>
              <H3 style={{ margin: 0 }}>Your Vendors</H3>
            </OrdersHeader>
            <TableContainer>
              <Table>
                <TableHead>
                  <tr>
                    <TableHeader>Vendor</TableHeader>
                    <TableHeader>Email</TableHeader>
                    <TableHeader>Commission</TableHeader>
                    <TableHeader>Orders</TableHeader>
                    <TableHeader>Value</TableHeader>
                  </tr>
                </TableHead>
                <TableBody>
                  {managedVendors.map((v) => {
                    const stats = ordersByVendor.get(v.id) ?? { count: 0, value: 0 };
                    return (
                      <TableRow key={v.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                        <TableCell>{`${v.firstName || ''} ${v.lastName || ''}`.trim() || v.email || `Vendor ${v.id}`}</TableCell>
                        <TableCell>{v.email ?? '—'}</TableCell>
                        <TableCell>{v.agentCommission !== undefined ? `${v.agentCommission}%` : '—'}</TableCell>
                        <TableCell>{stats.count}</TableCell>
                        <TableCell><Amount>{formatAmount(stats.value)}</Amount></TableCell>
                      </TableRow>
                    );
                  })}
                  {managedVendors.length === 0 && (
                    <TableRow initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                      <TableCell colSpan={5}><span style={{ color: colors.gray500 }}>No vendors under this agent yet</span></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </OrdersCard>
        </>
      )}
    </DashboardContainer>
  );
};
